import { useEffect, useRef, useState } from "react";
import useOnboardingStore from "../useOnboardingStore";

export const friendlyBankError = (raw = "") => {
  const s = raw.toLowerCase();
  if (s.includes("inactive")) return "This bank account is inactive. Please use an active account.";
  if (s.includes("invalid bank details") || s.includes("invalid account")) return "We couldn't verify these bank details. Please check your account number and IFSC code.";
  if (s.includes("ifsc")) return "Invalid IFSC code. Please double-check and try again.";
  return "Bank verification failed. Please check your details and try again.";
};

const stringValue = (values, substep) =>
  (values?.[substep?.substep_id]?.string_value ?? "").trim();

export const useBankAutofill = ({
  primitives,
  allValues,
  onChange,
}) => {
  const verifyBankDetails = useOnboardingStore((s) => s.verifyBankDetails);
  const {
    accNumPrim,
    confirmPrim,
    ifscPrim,
    holderPrim,
    bankNamePrim,
    bankAddressPrim,
    accountTypePrim,
  } = primitives;

  const canBankVerify = !!(accNumPrim && confirmPrim && ifscPrim && holderPrim && accountTypePrim);
  const accNumVal = stringValue(allValues, accNumPrim);
  const confirmVal = stringValue(allValues, confirmPrim);
  const ifscVal = stringValue(allValues, ifscPrim);
  const accountTypeVal = stringValue(allValues, accountTypePrim);
  const existingHolderName = stringValue(allValues, holderPrim);
  const existingBankName = stringValue(allValues, bankNamePrim);
  const existingBankAddress = stringValue(allValues, bankAddressPrim);

  const hasExistingBankAutofill =
    canBankVerify &&
    existingHolderName.length > 0 &&
    (!bankNamePrim || existingBankName.length > 0) &&
    (!bankAddressPrim || existingBankAddress.length > 0);

  const [bankVerifying, setBankVerifying] = useState(false);
  const [bankVerified, setBankVerified] = useState(hasExistingBankAutofill);
  const [bankError, setBankError] = useState("");

  const initialKey = hasExistingBankAutofill
    ? `${accNumVal}|${ifscVal}|${accountTypeVal}`
    : "";
  const verifyingRef = useRef(false);
  const lastVerifiedKeyRef = useRef(initialKey);

  const confirmMatch = accNumVal.length > 0 && confirmVal.length > 0 && confirmVal === accNumVal;
  const confirmMismatch = confirmVal.length > 0 && accNumVal.length > 0 && confirmVal !== accNumVal;
  const readyToVerify = canBankVerify && !bankVerified && confirmMatch && ifscVal.length > 0 && accountTypeVal.length > 0;

  const clearBankAutofill = () => {
    onChange(holderPrim.substep_id, { string_value: "" });
    if (bankNamePrim) onChange(bankNamePrim.substep_id, { string_value: "" });
    if (bankAddressPrim) onChange(bankAddressPrim.substep_id, { string_value: "" });
  };

  useEffect(() => {
    if (!bankVerified || !canBankVerify) return;
    const key = `${accNumVal}|${ifscVal}|${accountTypeVal}`;
    if (key !== lastVerifiedKeyRef.current) {
      setBankVerified(false);
      setBankError("");
      clearBankAutofill();
    }
    
  }, [accNumVal, ifscVal, accountTypeVal]);

  useEffect(() => {
    if (!readyToVerify || verifyingRef.current) return;
    const key = `${accNumVal}|${ifscVal}|${accountTypeVal}`;
    if (key === lastVerifiedKeyRef.current) return;

    const timer = setTimeout(async () => {
      if (!readyToVerify || verifyingRef.current) return;
      verifyingRef.current = true;
      setBankVerifying(true);
      setBankError("");

      const res = await verifyBankDetails(accNumVal, confirmVal, ifscVal, {
        requireBankName: !!bankNamePrim,
        requireBankAddress: !!bankAddressPrim,
      });
      setBankVerifying(false);
      verifyingRef.current = false;

      if (res.ok) {
        lastVerifiedKeyRef.current = key;
        setBankVerified(true);
        setBankError("");
        onChange(holderPrim.substep_id, { string_value: res.accountHolderName });
        if (bankNamePrim) onChange(bankNamePrim.substep_id, { string_value: res.bankName });
        if (bankAddressPrim) onChange(bankAddressPrim.substep_id, { string_value: res.bankAddress });
      } else {
        setBankVerified(false);
        setBankError(res.message);
        clearBankAutofill();
      }
    }, 600);

    return () => clearTimeout(timer);
    
  }, [readyToVerify, accNumVal, confirmVal, ifscVal, accountTypeVal]);

  return {
    canBankVerify,
    confirmMatch,
    confirmMismatch,
    bankVerifying,
    bankVerified,
    bankError,
  };
};
